/**
 * Extracts dealId and mipId from the current browser URL.
 *
 * This is the single source of truth for project context in API calls.
 * No sessionStorage, no React hooks — just reads the URL directly.
 *
 * Supported route patterns (pathname):
 *   /deal/:dealId/...
 *   /deal/:dealId/mip/:mipId/...
 *   /investment/:dealId/:mipId/...
 *   /documents/deal/:dealId/mip/:mipId/...
 *
 * Also reads from query params as fallback:
 *   ?mipId=xxx  (e.g. holdings detail, valuations sources & uses)
 *
 *
 *   This is pending a phase 2 refactor to unify all routes to a consistent pattern,
 *   at which point the query param fallback can be removed.
 */
export function getProjectIdsFromUrl(): { dealId: string | null; mipId: string | null } {
	const { pathname, search } = window.location;

	let dealId: string | null = null;
	let mipId: string | null = null;

	// /deal/:dealId (main app)
	const dealMatch = pathname.match(/\/deal\/([^/]+)/);
	if (dealMatch) {
		dealId = dealMatch[1];
	}

	// /deal/:dealId/mip/:mipId (main app MIP pages)
	const mipMatch = pathname.match(/\/mip\/([^/]+)/);
	if (mipMatch) {
		mipId = mipMatch[1];
	}

	// /investment/:dealId/:mipId (investor app)
	const investmentMatch = pathname.match(/\/investment\/([^/]+)\/([^/]+)/);
	if (investmentMatch) {
		dealId = investmentMatch[1];
		mipId = investmentMatch[2];
	}

	// Fallback: read mipId from query params if not found in pathname.
	// Some pages (holdings detail, valuations) pass mipId as a query param.
	if (!mipId) {
		const params = new URLSearchParams(search);
		mipId = params.get('mipId') ?? null;
	}

	return { dealId, mipId };
}
